import { z } from 'zod';
import * as dt from '../data.types.js';
import { uuidSchema, } from './primitives.js';
// Stripe Product/Price Management Schemas
// ========================================
const billingCycleSchema = z.nativeEnum(dt.BILLING_CYCLE);
const resourceTypeSchema = z.nativeEnum(dt.BILLING_RESOURCE_TYPE);
// Feature pricing structure - each feature can have an optional price
export const FeaturePricingSchema = z.object({
    enabled: z.boolean(),
    price: z.number().min(0).optional(), // Monthly price for this feature
});
// Create Stripe Product Request
export const CreateStripeProductRequestSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    tenantId: uuidSchema.optional(),
    // Base pricing
    monthlyBasePrice: z.number().min(0),
    yearlyBasePrice: z.number().min(0).optional(),
    billingCycle: billingCycleSchema,
    trialPeriodDays: z.number().int().min(1).max(365).optional(),
    trialStartDate: z.coerce.date().optional(),
    trialEndDate: z.coerce.date().optional(),
    // Base quotas
    baseUsers: z.number().int().min(1),
    baseContainers: z.number().int().min(1),
    baseWafers: z.number().int().min(1),
    baseAxonGateways: z.number().int().min(0),
    baseStorageTb: z.number().min(0.1),
    // Overage pricing (per unit above base)
    pricePerUser: z.number().min(0).optional(),
    pricePerContainer: z.number().min(0).optional(),
    pricePer1000Wafers: z.number().min(0).optional(),
    pricePerAxonGateway: z.number().min(0).optional(),
    pricePerTb: z.number().min(0).optional(),
    // Encryption pricing
    customerManagedEncryption: z.boolean().optional(),
    encryptionFee: z.number().min(0).optional(),
    // Feature flags with pricing
    features: z.record(FeaturePricingSchema).optional(),
});
// Update Stripe Product Request
export const UpdateStripeProductRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    active: z.boolean().optional(),
});
// List Stripe Products Request
export const ListStripeProductsRequestSchema = z.object({
    tenantId: uuidSchema.optional(),
    active: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    startingAfter: z.string().optional(),
});
// Create Stripe Price Request
export const CreateStripePriceRequestSchema = z.object({
    unitAmount: z.number().min(0),
    resourceType: resourceTypeSchema,
    billingCycle: billingCycleSchema,
    recurring: z.boolean(),
    usageType: z.enum(['licensed', 'metered']).optional(),
});
// Stripe Product Response
export const StripeProductResponseSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    active: z.boolean(),
    metadata: z.record(z.string()),
    created: z.number(),
    prices: z.array(z.object({
        id: z.string(),
        active: z.boolean(),
        currency: z.string(),
        unit_amount: z.number().nullable(),
        recurring: z.object({
            interval: z.string(),
            usage_type: z.string().optional(),
        }).nullable(),
        metadata: z.record(z.string()),
    })),
});
// List Stripe Products Response
export const ListStripeProductsResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        products: z.array(StripeProductResponseSchema),
        hasMore: z.boolean(),
    }),
});
// Single Stripe Product Response
export const SingleStripeProductResponseSchema = z.object({
    success: z.boolean(),
    data: StripeProductResponseSchema,
    message: z.string().optional(),
});
export const CreateStripePriceResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        id: z.string(),
        active: z.boolean(),
        currency: z.string(),
        unit_amount: z.number().nullable(),
        recurring: z.object({
            interval: z.string(),
            usage_type: z.string().optional(),
        }).nullable(),
        metadata: z.record(z.string()),
    }),
    message: z.string().optional(),
});
//# sourceMappingURL=stripe-products.schemas.js.map